// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import React from "react";
import { css } from "styled-components";

const enum fields {
    display = "display",
    wrap = "wrap",
    justifyContent = "justifyContent",
    alignItems = "alignItems",
}

type Props = WidgetProps & {
    fields: {
        [fields.display]: string;
        [fields.wrap]: string;
        [fields.justifyContent]: string;
        [fields.alignItems]: string;
    };

    id: string;
};

const FlexDisplay: React.FC<Props> = (props: Props) => {
    const {
        fields: { display, justifyContent, alignItems, wrap },
        id,
    } = props;

    return (
        <StyledWrapper
            css={css`
                & > div {
                    display: flex;
                    flex-wrap: ${wrap};
                    flex-direction: ${display};
                    justify-content: ${justifyContent};
                    align-items: ${alignItems};
                }
            `}
        >
            <Zone contentId={id} zoneName="Content" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: FlexDisplay,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.display,
                displayName: "Display Direction",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Row",
                        value: "row",
                    },
                    {
                        displayName: "Row Reverse",
                        value: "row-reverse",
                    },
                    {
                        displayName: "Column",
                        value: "column",
                    },
                    {
                        displayName: "Column Reverse",
                        value: "column-reverse",
                    },
                ],
                isRequired: true,
                defaultValue: "row",
                fieldType: "General",
            },
            {
                name: fields.wrap,
                displayName: "elements wrap",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Wrap",
                        value: "wrap",
                    },
                    {
                        displayName: "No Wrap",
                        value: "no-wrap",
                    },
                ],
                isRequired: true,
                defaultValue: "row",
                fieldType: "General",
            },
            {
                name: fields.justifyContent,
                displayName: "Justify Content",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Default",
                        value: "initial",
                    },
                    {
                        displayName: "Flex Start",
                        value: "flex-start",
                    },
                    {
                        displayName: "Flex End",
                        value: "flex-end",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Space Between",
                        value: "space-between",
                    },
                    {
                        displayName: "Space Around",
                        value: "space-around",
                    },
                ],
                isRequired: true,
                defaultValue: "initial",
                fieldType: "General",
            },
            {
                name: fields.alignItems,
                displayName: "Align Items",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Default",
                        value: "initial",
                    },
                    {
                        displayName: "Flex Start",
                        value: "flex-start",
                    },
                    {
                        displayName: "Flex End",
                        value: "flex-end",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                ],
                isRequired: true,
                defaultValue: "initial",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
